import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { formatAmount } from '../lib/stax';
import { Building2, MapPin, Phone, Mail, Globe, Users, Award, Calendar, Wrench, Check } from 'lucide-react';

interface MembershipPlan {
  id: string;
  plan_code: string;
  plan_name: string;
  description: string;
  base_price_monthly_1_pm: number;
  base_price_annual_1_pm: number;
  base_price_monthly_2_pm: number;
  base_price_annual_2_pm: number;
  additional_system_monthly_1_pm: number;
  additional_system_annual_1_pm: number;
  additional_system_monthly_2_pm: number;
  additional_system_annual_2_pm: number;
  features: string[];
  coverage_details: any;
  sort_order: number;
}

interface CompanyData {
  id: string;
  name: string;
  fullName: string;
  location: string;
  region: string;
  state: string;
  yearsInBusiness: string;
  established: string;
  slogan: string;
  services: string[];
  specialties: string[];
  serviceCities: {
    major: string[];
    all: string[];
  };
  contact: {
    phone?: string;
    email?: string;
    website?: string;
    address?: string;
  };
  memberships: {
    name: string;
    price: string;
    badge?: string;
    savings?: string;
    benefits: string[];
  }[];
  differentiators: string[];
  guarantees: string[];
}

const calculateYearsSince = (establishedYear: string): number => {
  const currentYear = new Date().getFullYear();
  return currentYear - parseInt(establishedYear);
};

const deriveCompanyId = (companyName: string, companyData: any): string => {
  const website = companyData?.contact?.website;

  if (typeof website === 'string' && website.length > 0) {
    try {
      const hostname = new URL(website).hostname.replace(/^www\./, '');
      const slug = hostname.split('.')[0];
      if (slug) return slug.toLowerCase();
    } catch {
      // Ignore invalid URLs
    }
  }

  const normalizedName = companyName.toLowerCase();
  if (normalizedName.includes('hales')) return 'halesac';
  if (normalizedName.includes('abraham')) return 'abrahamac';
  if (normalizedName.includes('myccs') || normalizedName.includes('climate')) return 'myccs';

  return companyName.toLowerCase().replace(/[^a-z0-9]/g, '');
};

const pricingInfo = {
  diagnosticFees: {
    standard: '$99',
    member: '$69',
    optimum: 'FREE'
  },
  maintenance: {
    premier: '$275',
    essential: '$129'
  },
  ductCleaning: {
    standard: '$624 per system',
    member: '$499.20 per system',
    includes: ['Professional Rotobrush cleaning', 'FREE antimicrobial fogging', 'FREE dryer vent cleaning']
  }
};

export default function CompanyInfoPage() {
  const [companies, setCompanies] = useState<CompanyData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('halesac');
  const [membershipPlans, setMembershipPlans] = useState<MembershipPlan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [billingFrequency, setBillingFrequency] = useState<'monthly' | 'annual'>('monthly');
  const [pmFrequency, setPmFrequency] = useState<1 | 2>(1);
  const [systemsCount, setSystemsCount] = useState(1);
  const [isEditing, setIsEditing] = useState(false);
  const [editedCities, setEditedCities] = useState('');

  useEffect(() => {
    fetchCompanies();
    loadMembershipPlans();
  }, []);

  async function fetchCompanies() {
    const { data, error } = await supabase.from('company_info_reference').select('*');
    if (data) {
      const formattedData = data.map((c: any) => {
        const companyData = c.company_data || {};
        return {
          id: deriveCompanyId(c.company_name, companyData),
          name: c.company_name,
          fullName: companyData.fullName || '',
          location: companyData.location || '',
          region: companyData.region || '',
          state: companyData.state || '',
          yearsInBusiness: companyData.yearsInBusiness || '',
          established: companyData.established || '2000',
          slogan: companyData.slogan || '',
          services: companyData.services || [],
          specialties: companyData.specialties || [],
          serviceCities: {
            major: companyData.service_cities ? (companyData.service_cities.slice(0, 10) || []) : [],
            all: companyData.service_cities ? (companyData.service_cities.slice(10) || []) : [],
          },
          contact: companyData.contact || {},
          memberships: companyData.memberships || [],
          differentiators: companyData.differentiators || [],
          guarantees: companyData.guarantees || [],
        };
      });
      setCompanies(formattedData);

      // Ensure the selected company id actually exists.
      if (formattedData.length > 0 && !formattedData.some((c: any) => c.id === selectedCompanyId)) {
        setSelectedCompanyId(formattedData[0].id);
      }
    }
    setLoading(false);
  }

  async function loadMembershipPlans() {
    try {
      const { data, error } = await supabase
        .from('membership_plans')
        .select('*')
        .eq('company_id', 'climate')
        .eq('is_active', true)
        .order('sort_order');

      if (error) throw error;
      setMembershipPlans(data || []);
    } catch (error) {
      console.error('Error loading membership plans:', error);
    } finally {
      setLoadingPlans(false);
    }
  }

  function calculatePrice(plan: MembershipPlan): number {
    let basePrice: number;
    
    if (pmFrequency === 1) {
      basePrice = billingFrequency === 'monthly' 
        ? plan.base_price_monthly_1_pm 
        : plan.base_price_annual_1_pm;
    } else {
      basePrice = billingFrequency === 'monthly'
        ? plan.base_price_monthly_2_pm
        : plan.base_price_annual_2_pm;
    }

    if (systemsCount > 1) {
      let additionalPrice: number;
      
      if (pmFrequency === 1) {
        additionalPrice = billingFrequency === 'monthly'
          ? plan.additional_system_monthly_1_pm
          : plan.additional_system_annual_1_pm;
      } else {
        additionalPrice = billingFrequency === 'monthly'
          ? plan.additional_system_monthly_2_pm
          : plan.additional_system_annual_2_pm;
      }
      
      basePrice += additionalPrice * (systemsCount - 1);
    }

    return basePrice;
  }

  const company = companies.find(c => c.id === selectedCompanyId);

  useEffect(() => {
    if (company) {
      setEditedCities([...company.serviceCities.major, ...company.serviceCities.all].join(', '));
    }
  }, [company]);

  const handleSave = async () => {
    if (!company) return;

    const cities = editedCities.split(',').map(c => c.trim()).filter(Boolean);

    const updatedCompanyData = {
      fullName: company.fullName,
      location: company.location,
      region: company.region,
      state: company.state,
      yearsInBusiness: company.yearsInBusiness,
      established: company.established,
      slogan: company.slogan,
      services: company.services,
      specialties: company.specialties,
      service_cities: cities,
      contact: company.contact,
      memberships: company.memberships,
      differentiators: company.differentiators,
      guarantees: company.guarantees,
    };

    const { error } = await supabase
      .from('company_info_reference')
      .update({ company_data: updatedCompanyData })
      .eq('company_name', company.name);

    if (!error) {
      setIsEditing(false);
      fetchCompanies();
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!company) {
    return <div>Company not found</div>;
  }

  const yearsInService = calculateYearsSince(company.established);
  
  const offersPlumbingOrAppliance = company.services?.some(
    (service: string) => service.toLowerCase().includes('plumbing') || service.toLowerCase().includes('appliance')
  ) || false;
  
  const offersOptimumMembership = company.memberships?.some(
    (membership: { name: string; }) => membership.name.toLowerCase().includes('optimum')
  ) || false;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Company Information</h1>
          <p className="mt-1 text-sm text-gray-500">
            Comprehensive details about our family of companies
          </p>
        </div>
      </div>

      {/* Company Selector */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex space-x-4">
          {companies.map(comp => (
            <button
              key={comp.id}
              onClick={() => setSelectedCompanyId(comp.id)}
              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                selectedCompanyId === comp.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {comp.name}
            </button>
          ))}
        </div>
      </div>

      {/* Company Overview */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{company.fullName}</h2>
            <p className="mt-1 text-lg text-gray-600 italic">"{company.slogan}"</p>
            <p className="mt-1 text-md text-gray-600">{company.location}</p>
          </div>
          <div className="flex items-center space-x-2 bg-green-100 px-4 py-2 rounded-lg">
            <Award className="h-5 w-5 text-green-600" />
            <span className="font-semibold text-green-900">Est. {company.established}</span>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Contact Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
            <div className="space-y-3">
              {company.contact?.phone && (
                <div className="flex items-center text-gray-600">
                  <Phone className="h-5 w-5 mr-3 text-blue-600" />
                  <a href={`tel:${company.contact.phone}`} className="hover:text-blue-600">
                    {company.contact.phone}
                  </a>
                </div>
              )}
              {company.contact?.website && (
                <div className="flex items-center text-gray-600">
                  <Globe className="h-5 w-5 mr-3 text-blue-600" />
                  <a href={company.contact.website} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600">
                    {company.contact.website}
                  </a>
                </div>
              )}
              {company.contact?.email && (
                <div className="flex items-center text-gray-600">
                  <Mail className="h-5 w-5 mr-3 text-blue-600" />
                  <a href={`mailto:${company.contact.email}`} className="hover:text-blue-600">
                    {company.contact.email}
                  </a>
                </div>
              )}
              {company.contact?.address && (
                <div className="flex items-center text-gray-600">
                  <MapPin className="h-5 w-5 mr-3 text-blue-600" />
                  <span>{company.contact.address}</span>
                </div>
              )}
            </div>
          </div>

          {/* Key Differentiators */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">What Sets Us Apart</h3>
            <ul className="space-y-2">
              {(company.differentiators || []).slice(0, 4).map((diff, idx) => {
                // Replace year placeholders with calculated years
                let displayText = diff || '';
                if (typeof diff === 'string' && (diff.includes('years serving') || diff.includes('years in'))) {
                  displayText = diff.replace(/\d+\+/, `${yearsInService}+`);
                }
                return (
                  <li key={idx} className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    <span className="text-gray-600 text-sm">{displayText}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>

      {/* Services Offered */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center mb-4">
          <Wrench className="h-6 w-6 text-blue-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Services Offered</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {(company.services || []).filter(service => service && typeof service === 'string').map((service, idx) => (
            <div key={idx} className="flex items-center p-3 bg-gray-50 rounded-lg">
              <div className="h-2 w-2 bg-blue-600 rounded-full mr-3"></div>
              <span className="text-sm text-gray-700">{service}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Service Areas */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <MapPin className="h-6 w-6 text-blue-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Service Areas</h3>
          </div>
          <button onClick={() => setIsEditing(!isEditing)} className="text-sm text-blue-600">
            {isEditing ? 'Cancel' : 'Edit'}
          </button>
        </div>
        
        {isEditing ? (
          <div>
            <textarea
              className="w-full h-48 border p-2 rounded"
              value={editedCities}
              onChange={(e) => setEditedCities(e.target.value)}
            />
            <button onClick={handleSave} className="mt-2 px-4 py-2 bg-blue-600 text-white rounded">
              Save
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Major Cities (High Priority)</h4>
              <div className="flex flex-wrap gap-2">
                {(company.serviceCities.major || []).filter(city => city && typeof city === 'string').map((city, idx) => (
                  <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                    {city}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <h4 className="font-medium text-gray-900 mb-2">Additional Service Areas</h4>
              <div className="flex flex-wrap gap-2">
                {(company.serviceCities.all || []).filter(city => city && typeof city === 'string').map((city, idx) => (
                  <span key={idx} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                    {city}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Membership Programs */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center mb-6">
          <Users className="h-6 w-6 text-blue-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Membership Programs</h3>
        </div>

        {/* PM Frequency Toggle */}
        <div className="flex justify-center mb-6">
          <div className="bg-gray-50 rounded-lg border-2 border-blue-200 p-4 shadow-sm">
            <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
              Preventive Maintenance Visits Per Year
            </label>
            <div className="inline-flex rounded-lg border border-gray-300 p-1 bg-white">
              <button
                onClick={() => setPmFrequency(1)}
                className={`px-6 py-2 rounded-md font-medium transition-colors ${
                  pmFrequency === 1
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                1 Visit/Year
              </button>
              <button
                onClick={() => setPmFrequency(2)}
                className={`px-6 py-2 rounded-md font-medium transition-colors ${
                  pmFrequency === 2
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                2 Visits/Year
                <span className="ml-2 text-xs text-green-600 font-semibold">
                  (Recommended)
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Billing Frequency Toggle */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex rounded-lg border border-gray-300 p-1 bg-gray-50">
            <button
              onClick={() => setBillingFrequency('monthly')}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${
                billingFrequency === 'monthly'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingFrequency('annual')}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${
                billingFrequency === 'annual'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Annual
            </button>
          </div>
        </div>

        {/* Systems Count Selector */}
        <div className="flex justify-center mb-8">
          <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              How many HVAC systems do you have?
            </label>
            <select
              value={systemsCount}
              onChange={(e) => setSystemsCount(Number(e.target.value))}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                <option key={num} value={num}>
                  {num} {num === 1 ? 'System' : 'Systems'}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Plans Grid */}
        {loadingPlans ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-500">Loading membership plans...</div>
          </div>
        ) : membershipPlans.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {membershipPlans
              .filter((plan) => {
                // For Hales AC and Abraham AC, only show Standard/Comfort Club plan
                if (selectedCompanyId === 'halesac' || selectedCompanyId === 'abrahamac') {
                  return plan.plan_code === 'standard';
                }
                // For MyCCS, show all plans
                return true;
              })
              .map((plan) => {
              const price = calculatePrice(plan);
              const getPlanBadge = (code: string) => {
                if (code === 'standard') return { text: 'Best Value', color: 'bg-blue-500' };
                if (code === 'premier') return { text: 'Most Popular', color: 'bg-purple-500' };
                if (code === 'optimum') return { text: 'Premium', color: 'bg-amber-500' };
                return null;
              };
              const badge = getPlanBadge(plan.plan_code);

              return (
                <div key={plan.id} className="relative border-2 border-blue-200 rounded-lg p-4 hover:border-blue-400 transition-colors">
                  {badge && (
                    <span className={`absolute -top-3 left-4 px-3 py-1 ${badge.color} text-white text-xs font-semibold rounded-full`}>
                      {badge.text}
                    </span>
                  )}
                  <h4 className="text-lg font-bold text-gray-900 mb-2 mt-2">{plan.plan_name}</h4>
                  <p className="text-sm text-gray-600 mb-4">{plan.description}</p>
                  
                  {/* Dynamic Pricing */}
                  <div className="mb-4">
                    <div className="text-2xl font-bold text-blue-600">
                      {formatAmount(price)}
                      <span className="text-sm font-normal text-gray-600">
                        /{billingFrequency === 'monthly' ? 'mo' : 'yr'}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1 space-y-1">
                      {systemsCount > 1 && (
                        <div>Price for {systemsCount} systems</div>
                      )}
                      <div>{pmFrequency} preventive maintenance visit{pmFrequency > 1 ? 's' : ''}/year</div>
                    </div>
                  </div>

                  <ul className="space-y-2">
                    {(plan.features || []).map((feature, fidx) => (
                      <li key={fidx} className="flex items-start text-sm">
                        <Check className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            No membership plans available. Please check back later.
          </div>
        )}
      </div>

      {/* Standard Pricing */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center mb-4">
          <Calendar className="h-6 w-6 text-blue-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Standard Pricing</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="border rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3">Diagnostic Fees</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Non Member:</span>
                <span className="font-medium">{pricingInfo.diagnosticFees.standard}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Comfort Club:</span>
                <span className="font-medium text-green-600">{pricingInfo.diagnosticFees.member}</span>
              </div>
              {selectedCompanyId === 'myccs' && (
                <>
                  <div className="flex justify-between items-start">
                    <span className="text-gray-600">Premier Comfort Club:</span>
                    <div className="text-right">
                      <span className="font-medium text-green-600 block">$0 HVAC</span>
                      <span className="text-xs text-gray-500 mt-1 block">$69 Plumbing/Appliance</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-start">
                    <span className="text-gray-600">Optimum Comfort Club:</span>
                    <div className="text-right">
                      <span className="font-medium text-green-600 block">{pricingInfo.diagnosticFees.optimum}</span>
                      <span className="text-xs text-gray-500 mt-1 block">All services</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="border rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3">Maintenance</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Premier:</span>
                <span className="font-medium">{pricingInfo.maintenance.premier}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Essential:</span>
                <span className="font-medium">{pricingInfo.maintenance.essential}</span>
              </div>
            </div>
          </div>

          <div className="border rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3">Duct Cleaning</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Standard:</span>
                <span className="font-medium">{pricingInfo.ductCleaning.standard}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Member:</span>
                <span className="font-medium text-green-600">{pricingInfo.ductCleaning.member}</span>
              </div>
              <div className="mt-2 pt-2 border-t">
                <p className="text-xs text-gray-500 font-medium mb-1">Includes:</p>
                {pricingInfo.ductCleaning.includes.map((item, idx) => (
                  <p key={idx} className="text-xs text-gray-600">• {item}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Guarantees */}
      <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Our Guarantees</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {company.guarantees?.map((guarantee, idx) => (
            <div key={idx} className="flex items-center bg-white rounded-lg p-4 shadow-sm">
              <Award className="h-8 w-8 text-green-600 mr-3 flex-shrink-0" />
              <span className="font-medium text-gray-900">{guarantee}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
